import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AiBudgetApi } from '@agendor/shared';
import { Button, ConfirmDialog, formatCurrency, vars } from '@agendor/ui';
import BusinessService, {
    AutoRechargeNoCreditCardError,
    AutoRechargeWouldChargeNowError,
    BusinessDetails,
} from '../business-service';
import { Checkbox, CheckboxLabel, Divider, HelperText } from '../components/Input';
import { daysFromToday } from '../date-utils';
import { useToast } from '../toast-context';
import CreditPurchaseModal from './CreditPurchaseModal';
import type { AiBudgetState } from './AssistantSection';

type CreditBudgetCardProps = {
    service: BusinessService | null;
    business: BusinessDetails;
    state: AiBudgetState;
    onLoad: () => Promise<void> | void;
    onRefresh: () => Promise<void> | void;
    onReloadBusiness: () => Promise<void> | void;
};

const Section = styled.div`
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid ${vars.dividerColor};
`;

const Card = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const StatusMessage = styled.div`
    text-align: center;
    padding: 24px;
    color: #888;
`;

const BucketSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BucketHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
`;

const BucketLabel = styled.span`
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    font-weight: 500;
`;

type Tone = 'normal' | 'warning' | 'danger';

const toneTextColor = (tone: Tone): string =>
    tone === 'danger' ? vars.dangerColorDark : tone === 'warning' ? vars.warningColor : '#666';

const toneFillColor = (tone: Tone): string =>
    tone === 'danger' ? vars.dangerColor : tone === 'warning' ? vars.warningColor : vars.primaryColor;

const BucketUsage = styled.span<{ $tone: Tone }>`
    font-size: 0.85rem;
    color: ${(p) => toneTextColor(p.$tone)};
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 8px;
    background-color: ${vars.backgroundColorDark};
    border-radius: 4px;
    overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number; $tone: Tone }>`
    width: ${(p) => p.$percent}%;
    height: 100%;
    background-color: ${(p) => toneFillColor(p.$tone)};
    transition: width 0.3s ease;
`;

const SubText = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

// The shared divider carries its own vertical margin; the card already spaces
// its children with a gap.
const AutoRechargeDivider = styled(Divider)`
    margin: 0;
`;

const AutoRechargeTitle = styled.span`
    font-weight: 500;
`;

const AutoRechargeHint = styled(HelperText)`
    /* Lines up under the label: the box is 18px and the label gap is 8px. */
    margin-left: 26px;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: #888;
`;

const RefreshIconButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
    padding: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:not(:disabled):hover {
        background-color: ${vars.backgroundColorBright};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function formatRelativePast(timestamp: number, now: number): string {
    const diffMs = Math.max(0, now - timestamp);
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return 'agora há pouco';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes === 1 ? 'há 1 minuto' : `há ${minutes} minutos`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? 'há 1 hora' : `há ${hours} horas`;
    const days = Math.floor(hours / 24);
    return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

// Owns its own `now` state so a 30s tick only re-renders this text node,
// not the whole card. Cleans up on unmount.
function RelativeTimePast({ timestamp }: { timestamp: number }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);
    return <>{formatRelativePast(timestamp, now)}</>;
}

function RefreshIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            style={{ width: 14, height: 14 }}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
        </svg>
    );
}

export default function CreditBudgetCard({
    service,
    business,
    state,
    onLoad,
    onRefresh,
    onReloadBusiness,
}: CreditBudgetCardProps) {
    const [modalOpen, setModalOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [savingAutoRecharge, setSavingAutoRecharge] = useState(false);
    const [confirmChargeNow, setConfirmChargeNow] = useState(false);
    const { showToast } = useToast();

    const hasAiBudget = business.subscription?.hasAiBudget ?? false;

    if (!hasAiBudget) return null;

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    };

    // The setting persists on toggle instead of on a save button: the checkbox
    // reflects what the server knows, so the business is reloaded right after.
    const handleToggleAutoRecharge = async (enabled: boolean, allowChargingNow?: boolean) => {
        if (!service || savingAutoRecharge) return;
        setSavingAutoRecharge(true);
        try {
            await service.setAutoRechargeEnabled({ enabled, allowChargingNow });
            setConfirmChargeNow(false);
            await onReloadBusiness();
            // A charge just landed, so the meter on screen is a cycle behind.
            if (allowChargingNow) await onRefresh();
        } catch (error) {
            console.error('Failed to save auto-recharge', error);
            // Only an unasked refusal opens the dialog: one that survives the
            // confirmation would put the same question back on screen and loop with
            // nothing to explain it. Every other outcome closes it.
            const askFirst = error instanceof AutoRechargeWouldChargeNowError && !allowChargingNow;
            setConfirmChargeNow(askFirst);
            if (askFirst) return;

            if (error instanceof AutoRechargeNoCreditCardError) {
                // The card went away since this screen loaded — a declined renewal, or
                // the owner removing it elsewhere. Nothing is wrong with the request, so
                // it is a warning, and reloading drops the row into its no-card state
                // instead of leaving a checkbox that looks like it should work.
                showToast(
                    'Pague a próxima cobrança do plano com cartão de crédito para ativar a recarga automática.',
                    'warning',
                );
                await onReloadBusiness();
            } else {
                showToast('Não foi possível salvar a configuração.', 'error');
            }
        } finally {
            setSavingAutoRecharge(false);
        }
    };

    const renderAutoRecharge = () => {
        const autoRecharge = business.autoRecharge;
        if (!autoRecharge) return null;
        const { enabled } = autoRecharge;
        const creditCard = business.subscription?.creditCard ?? null;
        const price = formatCurrency(AiBudgetApi.DEFAULT_PACK.priceInCents);
        return (
            <>
                <AutoRechargeDivider />
                <BucketSection data-testid="auto-recharge">
                    <CheckboxLabel>
                        <Checkbox
                            data-testid="auto-recharge-checkbox"
                            checked={enabled}
                            disabled={creditCard === null || savingAutoRecharge}
                            onChange={(event) => handleToggleAutoRecharge(event.target.checked)}
                        />
                        <AutoRechargeTitle>Recarga automática</AutoRechargeTitle>
                    </CheckboxLabel>
                    {/* Says what the setting does, not what state it is in: copy that
                        tracks the checkbox reflows the card on every toggle and reads
                        stale for as long as the request is in flight. */}
                    {creditCard === null ? (
                        <AutoRechargeHint data-testid="auto-recharge-hint">
                            Pague a próxima cobrança do plano com cartão de crédito para ativar.
                        </AutoRechargeHint>
                    ) : (
                        <AutoRechargeHint>
                            Quando os créditos estiverem acabando, recarregamos {price} no cartão {creditCard.brand}{' '}
                            final {creditCard.last4}.
                        </AutoRechargeHint>
                    )}
                </BucketSection>
            </>
        );
    };

    const renderContent = () => {
        if (state.status === 'loading') {
            return <StatusMessage data-testid="credit-budget-loading">Carregando...</StatusMessage>;
        }
        if (state.status === 'error') {
            return (
                <StatusMessage data-testid="credit-budget-error">
                    Não foi possível carregar os créditos.
                    <br />
                    <Button onClick={onLoad} style={{ marginTop: '12px' }}>
                        Tentar novamente
                    </Button>
                </StatusMessage>
            );
        }

        const { totalCredits, creditsUsed, rechargeBalance, cycleEndsAt } = state.snapshot;

        const monthlyUsed = Math.min(creditsUsed, totalCredits);
        const monthlyPercent = totalCredits > 0 ? clamp((monthlyUsed / totalCredits) * 100, 0, 100) : 0;
        const monthlyTone: Tone =
            monthlyUsed >= totalCredits
                ? 'danger'
                : monthlyPercent >= AiBudgetApi.CREDIT_WARNING_THRESHOLD * 100
                  ? 'warning'
                  : 'normal';

        const extraBucketSize = AiBudgetApi.extraBucketSize(state.snapshot);
        const showExtra = extraBucketSize > 0;
        const extraRemaining = Math.max(0, rechargeBalance);
        const extraPercent = showExtra
            ? clamp(((extraBucketSize - extraRemaining) / extraBucketSize) * 100, 0, 100)
            : 0;
        const extraTone: Tone =
            rechargeBalance <= 0
                ? 'danger'
                : rechargeBalance <= AiBudgetApi.EXTRA_WARNING_THRESHOLD
                  ? 'warning'
                  : 'normal';

        const remainingDays = Math.max(0, daysFromToday(cycleEndsAt));

        return (
            <>
                <BucketSection data-testid="credit-budget-monthly">
                    <BucketHeader>
                        <BucketLabel>Plano mensal</BucketLabel>
                        <BucketUsage $tone={monthlyTone}>{Math.round(monthlyPercent)}% usado</BucketUsage>
                    </BucketHeader>
                    <ProgressTrack>
                        <ProgressFill $percent={monthlyPercent} $tone={monthlyTone} />
                    </ProgressTrack>
                    <SubText>
                        {remainingDays === 0
                            ? 'Ciclo termina hoje'
                            : remainingDays === 1
                              ? 'Ciclo renova em 1 dia'
                              : `Ciclo renova em ${remainingDays} dias`}
                    </SubText>
                </BucketSection>
                {showExtra && (
                    <BucketSection data-testid="credit-budget-extra">
                        <BucketHeader>
                            <BucketLabel>Créditos extras</BucketLabel>
                            <BucketUsage $tone={extraTone}>
                                {formatCurrency(AiBudgetApi.creditsPriceInCents(extraRemaining))} restantes
                            </BucketUsage>
                        </BucketHeader>
                        <ProgressTrack>
                            <ProgressFill $percent={extraPercent} $tone={extraTone} />
                        </ProgressTrack>
                    </BucketSection>
                )}
                {renderAutoRecharge()}
                <Footer data-testid="credit-budget-footer">
                    <span>
                        Atualizado <RelativeTimePast timestamp={state.lastUpdatedAt} />
                    </span>
                    <RefreshIconButton
                        type="button"
                        aria-label="Atualizar"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        <RefreshIcon />
                    </RefreshIconButton>
                </Footer>
            </>
        );
    };

    const isLoaded = state.status === 'loaded';
    const suspended = isLoaded && AiBudgetApi.isSuspended(state.snapshot);

    return (
        <Section>
            <Card data-testid="credit-budget-card">
                <Header>
                    <Title>Créditos de IA</Title>
                    {isLoaded && !suspended && (
                        <Button data-testid="credit-budget-purchase-btn" onClick={() => setModalOpen(true)}>
                            Comprar créditos
                        </Button>
                    )}
                </Header>
                {renderContent()}
            </Card>
            <CreditPurchaseModal
                service={service}
                creditCard={business.subscription?.creditCard ?? null}
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    onRefresh();
                }}
            />
            <ConfirmDialog
                isOpen={confirmChargeNow}
                title="Recarregar agora?"
                message={
                    business.subscription?.creditCard
                        ? `Seus créditos já estão no fim, então ativar a recarga automática cobra ` +
                          `${formatCurrency(AiBudgetApi.DEFAULT_PACK.priceInCents)} no cartão ` +
                          `${business.subscription.creditCard.brand} final ` +
                          `${business.subscription.creditCard.last4} agora. Depois disso, recarregamos sozinhos ` +
                          `sempre que estiverem acabando.`
                        : ''
                }
                confirmText="Ativar e recarregar"
                loadingText="Recarregando..."
                confirmDanger={false}
                isLoading={savingAutoRecharge}
                onConfirm={() => handleToggleAutoRecharge(true, true)}
                onCancel={() => setConfirmChargeNow(false)}
            />
        </Section>
    );
}
